import { createContext, useContext, useEffect, useState, useCallback } from "react";
import userApi from "@/lib/userApi";

const AppAuthContext = createContext(null);
const TOKEN_KEY = "pd_user_token";

export function AppAuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = logged-in
  const [loading, setLoading] = useState(true);

  const check = useCallback(async () => {
    // CRITICAL: returning from Google OAuth — let AuthCallback exchange session_id first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await userApi.get("/account/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const login = async (email, password) => {
    const { data } = await userApi.post("/account/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await userApi.post("/account/register", payload);
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogleSession = async (sessionId) => {
    const { data } = await userApi.post("/account/google-session", { session_id: sessionId });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  return (
    <AppAuthContext.Provider value={{ user, loading, login, register, loginWithGoogleSession, logout, refresh: check }}>
      {children}
    </AppAuthContext.Provider>
  );
}

export function useAppAuth() {
  return useContext(AppAuthContext);
}
